import PropTypes from 'prop-types';
import React from 'react';
import {defineMessages, useIntl} from 'react-intl';

import Modal from '../../containers/modal.jsx';

import styles from './domi-car-burn-center.css';

const messages = defineMessages({
    title: {
        id: 'gui.domiCar.burnCenter.title',
        defaultMessage: 'Car Burn Center',
        description: 'Title of the Domi Car Scratch program burn center'
    },
    intro: {
        id: 'gui.domiCar.burnCenter.intro',
        defaultMessage: 'Review the program below before writing it to the car.',
        description: 'Introduction in the Domi Car burn center'
    },
    connected: {
        id: 'gui.domiCar.burnCenter.connected',
        defaultMessage: 'Car connected',
        description: 'Connected status in the Domi Car burn center'
    },
    disconnected: {
        id: 'gui.domiCar.burnCenter.disconnected',
        defaultMessage: 'Car not connected',
        description: 'Disconnected status in the Domi Car burn center'
    },
    previewTitle: {
        id: 'gui.domiCar.burnCenter.previewTitle',
        defaultMessage: 'Program to be burned',
        description: 'Heading above the compiled instruction preview'
    },
    instructionCount: {
        id: 'gui.domiCar.burnCenter.instructionCount',
        defaultMessage: '{count} instructions',
        description: 'Compiled instruction count in the Domi Car burn center'
    },
    preparing: {
        id: 'gui.domiCar.burnCenter.preparing',
        defaultMessage: 'Checking the Scratch program…',
        description: 'Status while compiling a Domi Car Scratch program preview'
    },
    ready: {
        id: 'gui.domiCar.burnCenter.ready',
        defaultMessage: 'Program checked. Ready to burn.',
        description: 'Status after a Domi Car Scratch program passes validation'
    },
    uploading: {
        id: 'gui.domiCar.burnCenter.uploading',
        defaultMessage: 'Writing the program to the car…',
        description: 'Status while writing a Scratch program to the Domi Car'
    },
    success: {
        id: 'gui.domiCar.burnCenter.success',
        defaultMessage: 'Burn successful. Power-on and controller events are now saved on the car.',
        description: 'Status after successfully writing a Scratch program to the Domi Car'
    },
    error: {
        id: 'gui.domiCar.burnCenter.error',
        defaultMessage: 'The program could not be burned.',
        description: 'Fallback error in the Domi Car burn center'
    },
    connectHelp: {
        id: 'gui.domiCar.burnCenter.connectHelp',
        defaultMessage: 'Connect the car in the Domi Car blocks before starting the burn.',
        description: 'Help shown when the Domi Car is not connected'
    },
    safety: {
        id: 'gui.domiCar.burnCenter.safety',
        defaultMessage: 'Safety: the car may move immediately after burning. Lift the wheels and clear the area first.',
        description: 'Safety warning in the Domi Car burn center'
    },
    close: {
        id: 'gui.domiCar.burnCenter.close',
        defaultMessage: 'Close Burn Center',
        description: 'Button that closes the Domi Car burn center'
    },
    start: {
        id: 'gui.domiCar.burnCenter.start',
        defaultMessage: 'Start burning',
        description: 'Button that starts writing a Scratch program to the Domi Car'
    },
    completed: {
        id: 'gui.domiCar.burnCenter.completed',
        defaultMessage: 'Burn complete',
        description: 'Disabled action button after a Domi Car burn completes'
    },
    moveForward: {
        id: 'gui.domiCar.burnCenter.moveForward',
        defaultMessage: 'Move forward',
        description: 'Compiled Domi Car forward instruction'
    },
    moveBackward: {
        id: 'gui.domiCar.burnCenter.moveBackward',
        defaultMessage: 'Move backward',
        description: 'Compiled Domi Car backward instruction'
    },
    turnLeft: {
        id: 'gui.domiCar.burnCenter.turnLeft',
        defaultMessage: 'Turn left',
        description: 'Compiled Domi Car left instruction'
    },
    turnRight: {
        id: 'gui.domiCar.burnCenter.turnRight',
        defaultMessage: 'Turn right',
        description: 'Compiled Domi Car right instruction'
    },
    wait: {
        id: 'gui.domiCar.burnCenter.wait',
        defaultMessage: 'Wait {seconds} seconds',
        description: 'Compiled Domi Car wait instruction'
    },
    stop: {
        id: 'gui.domiCar.burnCenter.stop',
        defaultMessage: 'Stop the car',
        description: 'Compiled Domi Car stop instruction'
    },
    lightOn: {
        id: 'gui.domiCar.burnCenter.lightOn',
        defaultMessage: 'Turn the car light on',
        description: 'Compiled Domi Car light-on instruction'
    },
    lightOff: {
        id: 'gui.domiCar.burnCenter.lightOff',
        defaultMessage: 'Turn the car light off',
        description: 'Compiled Domi Car light-off instruction'
    },
    lightToggle: {
        id: 'gui.domiCar.burnCenter.lightToggle',
        defaultMessage: 'Toggle the car light',
        description: 'Compiled Domi Car light-toggle instruction'
    },
    powerOnEvent: {
        id: 'gui.domiCar.burnCenter.powerOnEvent',
        defaultMessage: 'When the car powers on',
        description: 'Compiled Domi Car power-on event'
    },
    controllerPressed: {
        id: 'gui.domiCar.burnCenter.controllerPressed',
        defaultMessage: 'When controller {button} is pressed',
        description: 'Compiled Domi Controller button pressed event'
    },
    controllerReleased: {
        id: 'gui.domiCar.burnCenter.controllerReleased',
        defaultMessage: 'When controller {button} is released',
        description: 'Compiled Domi Controller button released event'
    }
});

const getInstructionLabel = (instruction, intl) => {
    switch (instruction) {
    case 'M F': return intl.formatMessage(messages.moveForward);
    case 'M B': return intl.formatMessage(messages.moveBackward);
    case 'M L': return intl.formatMessage(messages.turnLeft);
    case 'M R': return intl.formatMessage(messages.turnRight);
    case 'S': return intl.formatMessage(messages.stop);
    case 'L 1': return intl.formatMessage(messages.lightOn);
    case 'L 0': return intl.formatMessage(messages.lightOff);
    case 'L T': return intl.formatMessage(messages.lightToggle);
    case 'E START': return intl.formatMessage(messages.powerOnEvent);
    default:
        if (instruction.startsWith('E KEY ')) {
            const [, , button, action] = instruction.split(' ');
            return intl.formatMessage(
                action === 'PRESS' ? messages.controllerPressed : messages.controllerReleased,
                {button}
            );
        }
        if (instruction.startsWith('W ')) {
            const milliseconds = Number(instruction.slice(2));
            const seconds = Number((milliseconds / 1000).toFixed(2));
            return intl.formatMessage(messages.wait, {seconds});
        }
        return instruction;
    }
};

const phaseMessages = {
    preparing: messages.preparing,
    ready: messages.ready,
    invalid: messages.error,
    uploading: messages.uploading,
    success: messages.success,
    error: messages.error
};

const DomiCarBurnCenter = ({
    connected,
    instructionCount,
    instructions,
    isOpen,
    message,
    onClose,
    onStart,
    phase,
    progress
}) => {
    const intl = useIntl();
    if (!isOpen) return null;

    const canStart = connected && phase === 'ready' && instructions.length > 0;
    const showProgress = ['uploading', 'success', 'error'].includes(phase);
    const statusMessage = message || intl.formatMessage(phaseMessages[phase] || messages.preparing);
    const startLabel = phase === 'success' ? messages.completed : messages.start;

    return (
        <Modal
            className={styles.modal}
            contentLabel={intl.formatMessage(messages.title)}
            id="domi-car-burn-center"
            onRequestClose={onClose}
        >
            <div className={styles.body}>
                <div className={styles.intro}>
                    <p>{intl.formatMessage(messages.intro)}</p>
                    <span className={connected ? styles.connectedBadge : styles.disconnectedBadge}>
                        {intl.formatMessage(connected ? messages.connected : messages.disconnected)}
                    </span>
                </div>

                {connected ? null : (
                    <div className={styles.warningBox}>{intl.formatMessage(messages.connectHelp)}</div>
                )}

                <section className={styles.previewCard}>
                    <div className={styles.previewHeader}>
                        <h3>{intl.formatMessage(messages.previewTitle)}</h3>
                        <span>{intl.formatMessage(messages.instructionCount, {count: instructionCount})}</span>
                    </div>
                    {phase === 'preparing' ? (
                        <div className={styles.emptyPreview}>{intl.formatMessage(messages.preparing)}</div>
                    ) : instructions.length ? (
                        <ol className={styles.instructionList}>
                            {instructions.map((instruction, index) => (
                                <li key={`${index}-${instruction}`}>
                                    <span>{getInstructionLabel(instruction, intl)}</span>
                                    <code>{instruction}</code>
                                </li>
                            ))}
                        </ol>
                    ) : (
                        <div className={styles.emptyPreview}>{statusMessage}</div>
                    )}
                </section>

                <div className={styles.safetyBox}>{intl.formatMessage(messages.safety)}</div>

                {showProgress ? (
                    <section
                        aria-live="polite"
                        className={`${styles.progressCard} ${
                            phase === 'error' ? styles.progressError : ''
                        } ${phase === 'success' ? styles.progressSuccess : ''}`}
                    >
                        <div className={styles.progressLabels}>
                            <span>{statusMessage}</span>
                            <strong>{progress}%</strong>
                        </div>
                        <div
                            aria-label={statusMessage}
                            aria-valuemax="100"
                            aria-valuemin="0"
                            aria-valuenow={progress}
                            className={styles.progressTrack}
                            role="progressbar"
                        >
                            <div
                                className={styles.progressFill}
                                style={{width: `${progress}%`}}
                            />
                        </div>
                    </section>
                ) : null}

                <div className={styles.actions}>
                    <button
                        className={styles.secondaryButton}
                        disabled={phase === 'uploading'}
                        type="button"
                        onClick={onClose}
                    >
                        {intl.formatMessage(messages.close)}
                    </button>
                    <button
                        className={styles.primaryButton}
                        disabled={!canStart}
                        type="button"
                        onClick={onStart}
                    >
                        {intl.formatMessage(startLabel)}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

DomiCarBurnCenter.propTypes = {
    connected: PropTypes.bool,
    instructionCount: PropTypes.number,
    instructions: PropTypes.arrayOf(PropTypes.string),
    isOpen: PropTypes.bool,
    message: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onStart: PropTypes.func.isRequired,
    phase: PropTypes.oneOf(['preparing', 'ready', 'invalid', 'uploading', 'success', 'error']),
    progress: PropTypes.number
};

DomiCarBurnCenter.defaultProps = {
    connected: false,
    instructionCount: 0,
    instructions: [],
    isOpen: false,
    message: '',
    phase: 'preparing',
    progress: 0
};

export default DomiCarBurnCenter;
